import { callGeminiAPI } from './geminiFunction';
import { 
  Client, 
  ContentDocument, 
  SocialPost, 
  FAQ, 
  EmailTemplate,
  ContentTransformationRequest,
  ContentTransformationResponse,
  RepurposingAnalytics,
  // PHASE-02 START
  RunMeta
  // PHASE-02 END
} from '../types';
import { doc, setDoc, getDoc, collection, query, where, getDocs, deleteDoc, updateDoc, limit } from 'firebase/firestore';
import { db } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

export class ContentManagementService {
  /**
   * Save content to the client-specific library
   */
  async saveContent(
    content: {
      title: string;
      content: string;
      type: ContentDocument['type'];
      metadata?: any;
      // PHASE-02 START
      provenance?: RunMeta;
      // PHASE-02 END
    },
    clientId: string,
    userId: string,
    sourceConversationId?: string
  ): Promise<ContentDocument> {
    try {
      const metadata = content.metadata && typeof content.metadata === 'object'
        ? { ...content.metadata }
        : {};
      const artifactKey = typeof metadata.artifactKey === 'string' ? metadata.artifactKey.trim() : '';
      const lineageId = typeof metadata.lineageId === 'string' ? metadata.lineageId.trim() : '';
      let existingDocId: string | null = null;
      let existingData: ContentDocument | null = null;

      const findExisting = async (fieldPath: string, value: string) => {
        if (!value || existingDocId) return;
        const snapshot = await getDocs(query(
          collection(db, 'contentDocuments'),
          where('clientId', '==', clientId),
          where(fieldPath as any, '==', value),
          limit(1)
        ));
        const match = snapshot.docs[0];
        if (match) {
          existingDocId = match.id;
          existingData = { id: match.id, ...match.data() } as ContentDocument;
        }
      };

      await findExisting('artifactKey', artifactKey);
      await findExisting('metadata.artifactKey', artifactKey);
      await findExisting('lineageId', lineageId);
      await findExisting('metadata.lineageId', lineageId);

      const contentId = existingDocId || `content-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const nowIso = new Date().toISOString();
      const contentDoc: ContentDocument = {
        id: contentId,
        clientId,
        userId: existingData?.userId || userId,
        title: content.title,
        type: content.type,
        content: content.content,
        status: existingData?.status || 'draft',
        createdAt: existingData?.createdAt || nowIso,
        updatedAt: nowIso,
        metadata: {
          ...(existingData?.metadata || {}),
          ...metadata,
          ...(artifactKey ? { artifactKey } : {}),
          ...(lineageId || artifactKey ? { lineageId: lineageId || artifactKey } : {}),
        },
        tags: existingData?.tags || [],
        version: typeof metadata.version === 'number'
          ? metadata.version
          : existingData?.version || 1,
        ...(artifactKey ? { artifactKey } : {}),
        ...(lineageId || artifactKey ? { lineageId: lineageId || artifactKey } : {}),
        ...(existingData as any)?.reviewState ? { reviewState: (existingData as any).reviewState } : {},
        ...(existingData as any)?.lastRunStage ? { lastRunStage: (existingData as any).lastRunStage } : {},
        ...(existingData as any)?.lastRunUpdatedAt ? { lastRunUpdatedAt: (existingData as any).lastRunUpdatedAt } : {},
        ...(existingData as any)?.review ? { review: (existingData as any).review } : {},
        // PHASE-02 START
        ...(content.provenance ? { provenance: content.provenance } : existingData?.provenance ? { provenance: existingData.provenance } : {}),
        // PHASE-02 END
        // PHASE-03 START
        ...((content.provenance?.policyIssues?.length || existingData?.policy?.issues?.length)
          ? { policy: { issues: content.provenance?.policyIssues || existingData?.policy?.issues || [] } }
          : {}),
        // PHASE-03 END
        ...(sourceConversationId
          ? { sourceConversationId }
          : existingData?.sourceConversationId
            ? { sourceConversationId: existingData.sourceConversationId }
            : {})
      };

      const docRef = doc(collection(db, 'contentDocuments'), contentId);
      await setDoc(docRef, contentDoc);
      return contentDoc;
    } catch (error) {
      console.error('Error saving content:', error);
      throw new Error('Failed to save content to library');
    }
  }

  /**
   * Get all content for a specific client
   */
  async getClientContent(
    clientId: string, 
    userId: string,
    type?: ContentDocument['type']
  ): Promise<ContentDocument[]> {
    try {
      let q = query(
        collection(db, 'contentDocuments'),
        where('clientId', '==', clientId),
        where('userId', '==', userId)
      );

      if (type) {
        q = query(q, where('type', '==', type));
      }

      const querySnapshot = await getDocs(q);
      const content: ContentDocument[] = [];
      
      querySnapshot.forEach((doc) => {
        content.push({ id: doc.id, ...doc.data() } as ContentDocument);
      });

      // Sort by creation date (newest first)
      return content.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } catch (error) {
      console.error('Error fetching client content:', error);
      throw new Error('Failed to fetch content library');
    }
  }

  /**
   * Generate social media posts from press release content
   */
  async generateSocialPosts(
    pressReleaseContent: string,
    client: Client,
    platforms: Array<'twitter' | 'facebook' | 'linkedin' | 'instagram'> = ['twitter', 'facebook', 'linkedin']
  ): Promise<SocialPost[]> {
    try {
      const clientContext = this.getClientContext(client);
      
      const prompt = `
Transform this press release into engaging social media posts for different platforms.
${clientContext}

Press Release:
"${pressReleaseContent}"

Create social posts for: ${platforms.join(', ')}

Platform Guidelines:
- Twitter: Max 280 characters, engaging and direct
- Facebook: 1-2 paragraphs, conversational and engaging  
- LinkedIn: Professional tone, 1-3 paragraphs with industry insights
- Instagram: Visual-focused, include hashtag suggestions

Provide a JSON response:
{
  "posts": [
    {
      "platform": "twitter",
      "content": "post content here",
      "hashtags": ["#hashtag1", "#hashtag2"],
      "characterCount": 250
    }
  ]
}

Focus on:
1. Key messages from the press release
2. Platform-appropriate tone and length
3. Client's brand voice
4. Engaging calls-to-action
5. Relevant hashtags
`;

      const response = await callGeminiAPI(prompt, 'gemini-2.5-flash');
      
      // Parse the JSON response
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('Failed to parse social posts response');
      }

      const data = JSON.parse(jsonMatch[0]);
      
      return data.posts.map((post: any, index: number) => ({
        id: `social-${Date.now()}-${index}`,
        platform: post.platform,
        content: post.content,
        hashtags: post.hashtags || [],
        characterCount: post.characterCount || post.content.length,
        clientId: client.id,
        sourceContent: pressReleaseContent,
        createdAt: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error generating social posts:', error);
      throw new Error('Failed to generate social media posts');
    }
  }

  /**
   * Generate FAQ from client conversations and content
   */
  async generateFAQ(
    clientId: string,
    topic?: string,
    client?: Client
  ): Promise<FAQ[]> {
    try {
      // Get recent client content for context
      const recentContent = await this.getClientContent(clientId, 'any-user', undefined);
      const contentContext = recentContent
        .slice(0, 5)
        .map(doc => `${doc.title}: ${doc.content.substring(0, 200)}...`)
        .join('\n');

      const clientContext = client ? this.getClientContext(client) : '';
      const topicContext = topic ? `Focus on: ${topic}` : 'Cover general topics about the company';

      const prompt = `
Generate a comprehensive FAQ for this client based on their content and context.
${clientContext}
${topicContext}

Recent Client Content:
${contentContext}

Create 5-8 frequently asked questions that potential customers, media, or stakeholders might have.

Provide a JSON response:
{
  "faqs": [
    {
      "question": "What makes your company different?",
      "answer": "Detailed answer based on client context...",
      "category": "About Us"
    }
  ]
}

Categories to consider:
- About Us
- Products/Services  
- Company News
- Industry Insights
- Contact/Support

Make answers:
1. Informative and helpful
2. Consistent with brand voice
3. Based on actual client information
4. Professional yet accessible
`;

      const response = await callGeminiAPI(prompt, 'gemini-2.5-flash');
      
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('Failed to parse FAQ response');
      }

      const data = JSON.parse(jsonMatch[0]);
      
      return data.faqs.map((faq: any, index: number) => ({
        id: `faq-${Date.now()}-${index}`,
        question: faq.question,
        answer: faq.answer,
        category: faq.category || 'General',
        clientId,
        createdAt: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error generating FAQ:', error);
      throw new Error('Failed to generate FAQ');
    }
  }

  /**
   * Generate email response template
   */
  async generateEmailTemplate(
    context: {
      purpose: string; // e.g., "media inquiry response", "customer support"
      tone: 'formal' | 'casual' | 'professional';
      includeSignature?: boolean;
    },
    client: Client
  ): Promise<EmailTemplate> {
    try {
      const clientContext = this.getClientContext(client);

      const prompt = `
Create an email template for: ${context.purpose}
${clientContext}

Requirements:
- Tone: ${context.tone}
- Purpose: ${context.purpose}
- Include placeholder variables like [RECIPIENT_NAME], [SPECIFIC_TOPIC]
- ${context.includeSignature ? 'Include signature block' : 'No signature needed'}

Provide a JSON response:
{
  "subject": "Email subject with [VARIABLES] as needed",
  "body": "Email body with proper formatting and [PLACEHOLDER] variables",
  "variables": ["RECIPIENT_NAME", "SPECIFIC_TOPIC"],
  "signature": "Optional signature block"
}

Make the template:
1. Professional and on-brand
2. Easily customizable with variables
3. Appropriate for the stated purpose
4. Clear and actionable
`;

      const response = await callGeminiAPI(prompt, 'gemini-2.5-flash');
      
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('Failed to parse email template response');
      }

      const data = JSON.parse(jsonMatch[0]);
      
      return {
        id: `email-${Date.now()}`,
        name: `${context.purpose} Template`,
        subject: data.subject,
        body: data.body,
        variables: data.variables || [],
        signature: data.signature,
        clientId: client.id,
        purpose: context.purpose,
        tone: context.tone,
        createdAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error generating email template:', error);
      throw new Error('Failed to generate email template');
    }
  }

  /**
   * Update content status (draft → approved → published)
   */
  async updateContentStatus(
    contentId: string,
    status: ContentDocument['status'],
    userId: string
  ): Promise<void> {
    try {
      const docRef = doc(db, 'contentDocuments', contentId);
      await updateDoc(docRef, {
        status,
        updatedAt: new Date().toISOString(),
        ...(status === 'published' && { publishedAt: new Date().toISOString() })
      });
    } catch (error) {
      console.error('Error updating content status:', error);
      throw new Error('Failed to update content status');
    }
  }

  /**
   * Update content (title, content, tags)
   */
  async updateContent(
    contentId: string,
    updates: {
      title?: string;
      content?: string;
      tags?: string[];
    },
    userId: string
  ): Promise<void> {
    try {
      // First verify ownership
      const docRef = doc(db, 'contentDocuments', contentId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Content not found');
      }

      const existingContent = docSnap.data() as ContentDocument;
      if (existingContent.userId !== userId) {
        throw new Error('Not authorized to update this content');
      }

      // Prepare update object, only including defined values
      const updateData: any = {
        updatedAt: new Date().toISOString(),
        version: (existingContent.version || 1) + 1
      };

      if (updates.title !== undefined) {
        updateData.title = updates.title;
      }
      if (updates.content !== undefined) {
        updateData.content = updates.content;
        updateData['metadata.characterCount'] = updates.content.length;
      }
      if (updates.tags !== undefined) {
        updateData.tags = updates.tags;
      }

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating content:', error);
      throw new Error('Failed to update content');
    }
  }

  /**
   * Delete content from library
   */
  async deleteContent(contentId: string, userId: string): Promise<void> {
    try {
      // Verify ownership before deletion
      const docRef = doc(db, 'contentDocuments', contentId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Content not found');
      }

      const content = docSnap.data() as ContentDocument;
      if (content.userId !== userId) {
        throw new Error('Not authorized to delete this content');
      }

      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting content:', error);
      throw new Error('Failed to delete content');
    }
  }

  /**
   * Get client context for content generation
   */
  private getClientContext(client: Client): string {
    return `
CLIENT CONTEXT:
Company: ${client.name}
Industry: ${client.industry}
Brand Tone: ${client.context.brandTone}
Key Messages: ${client.context.keyMessages.join(', ')}
Target Audience: ${client.context.targetAudience.join(', ')}
Approved Language: ${client.context.approvedLanguage.join(', ')}
Forbidden Language: ${client.context.forbiddenLanguage.join(', ')}
`;
  }

  /**
   * Search content by title or content
   */
  async searchContent(
    query: string,
    clientId: string,
    userId: string
  ): Promise<ContentDocument[]> {
    try {
      // Get all client content and filter locally
      // In a production app, you'd want server-side search
      const allContent = await this.getClientContent(clientId, userId);
      
      const searchTerm = query.toLowerCase();
      return allContent.filter(content => 
        content.title.toLowerCase().includes(searchTerm) ||
        content.content.toLowerCase().includes(searchTerm) ||
        content.tags.some(tag => tag.toLowerCase().includes(searchTerm))
      );
    } catch (error) {
      console.error('Error searching content:', error);
      throw new Error('Failed to search content');
    }
  }

  /**
   * Phase 3: Repurpose content for multiple platforms
   */
  async repurposeContent(
    sourceContentId: string,
    targetPlatforms: string[],
    userId: string,
    settings?: {
      toneAdjustments?: Record<string, any>;
      keyMessages?: string[];
      cta?: string;
      targetAudience?: string[];
    }
  ): Promise<{
    variations: ContentDocument[];
    analytics: RepurposingAnalytics;
    suggestions: any;
  }> {
    try {
      // Get source content
      const sourceDoc = await getDoc(doc(db, 'contentDocuments', sourceContentId));
      if (!sourceDoc.exists()) {
        throw new Error('Source content not found');
      }

      const sourceData = sourceDoc.data() as ContentDocument;
      
      // Verify ownership
      if (sourceData.userId !== userId) {
        throw new Error('Not authorized to repurpose this content');
      }

      // Call the transformation function
      const functions = getFunctions();
      const batchGenerate = httpsCallable(functions, 'batchGenerateContent');
      
      const result = await batchGenerate({
        sourceContentId,
        platforms: targetPlatforms,
        userId,
        settings: settings || {
          toneAdjustments: {},
          keyMessages: [],
          cta: '',
          targetAudience: []
        }
      });

      const data = result.data as any;
      
      if (!data.success) {
        throw new Error(data.error || 'Content repurposing failed');
      }

      // Fetch the generated content documents
      const variations: ContentDocument[] = [];
      for (const variationId of data.variationIds) {
        const variationDoc = await getDoc(doc(db, 'contentDocuments', variationId));
        if (variationDoc.exists()) {
          variations.push({ id: variationDoc.id, ...variationDoc.data() } as ContentDocument);
        }
      }

      return {
        variations,
        analytics: data.analytics,
        suggestions: data.suggestions
      };

    } catch (error) {
      console.error('Error repurposing content:', error);
      throw new Error('Failed to repurpose content');
    }
  }

  /**
   * Phase 3: Get content variations (linked content pieces)
   */
  async getContentVariations(contentId: string, userId: string): Promise<ContentDocument[]> {
    try {
      // Get the source content
      const contentDoc = await getDoc(doc(db, 'contentDocuments', contentId));
      if (!contentDoc.exists()) {
        return [];
      }

      const contentData = contentDoc.data() as ContentDocument;
      
      // Verify ownership
      if (contentData.userId !== userId) {
        throw new Error('Not authorized to view this content');
      }

      const variations: ContentDocument[] = [];

      // If this is a source content, get its variations
      if (contentData.repurposing?.isSource && contentData.repurposing.variations) {
        for (const variation of contentData.repurposing.variations) {
          const variationDoc = await getDoc(doc(db, 'contentDocuments', variation.contentId));
          if (variationDoc.exists()) {
            variations.push({ id: variationDoc.id, ...variationDoc.data() } as ContentDocument);
          }
        }
      }

      // If this is a variation, get the source and other variations
      if (contentData.repurposing?.sourceContentId) {
        const sourceDoc = await getDoc(doc(db, 'contentDocuments', contentData.repurposing.sourceContentId));
        if (sourceDoc.exists()) {
          const sourceData = sourceDoc.data() as ContentDocument;
          
          // Add the source content
          variations.push({ id: sourceDoc.id, ...sourceData });
          
          // Add other variations
          if (sourceData.repurposing?.variations) {
            for (const variation of sourceData.repurposing.variations) {
              if (variation.contentId !== contentId) { // Don't include the current content
                const variationDoc = await getDoc(doc(db, 'contentDocuments', variation.contentId));
                if (variationDoc.exists()) {
                  variations.push({ id: variationDoc.id, ...variationDoc.data() } as ContentDocument);
                }
              }
            }
          }
        }
      }

      return variations;
    } catch (error) {
      console.error('Error getting content variations:', error);
      throw new Error('Failed to get content variations');
    }
  }

  /**
   * Phase 3: Get repurposing analytics for content
   */
  async getRepurposingAnalytics(contentId: string, userId: string): Promise<RepurposingAnalytics | null> {
    try {
      const contentDoc = await getDoc(doc(db, 'contentDocuments', contentId));
      if (!contentDoc.exists()) {
        return null;
      }

      const contentData = contentDoc.data() as ContentDocument;
      
      // Verify ownership
      if (contentData.userId !== userId) {
        throw new Error('Not authorized to view this content analytics');
      }

      return contentData.repurposing?.analytics || null;
    } catch (error) {
      console.error('Error getting repurposing analytics:', error);
      return null;
    }
  }

  /**
   * Phase 3: Update repurposing performance metrics
   */
  async updateVariationPerformance(
    contentId: string,
    userId: string,
    metrics: {
      views?: number;
      engagement?: number;
      clicks?: number;
    }
  ): Promise<void> {
    try {
      const docRef = doc(db, 'contentDocuments', contentId);
      const contentDoc = await getDoc(docRef);
      
      if (!contentDoc.exists()) {
        throw new Error('Content not found');
      }

      const contentData = contentDoc.data() as ContentDocument;
      
      // Verify ownership
      if (contentData.userId !== userId) {
        throw new Error('Not authorized to update this content');
      }

      // Update the performance metrics
      const updates: any = {
        updatedAt: new Date().toISOString()
      };

      if (metrics.views !== undefined) {
        updates['repurposing.analytics.totalReach'] = metrics.views;
      }
      if (metrics.engagement !== undefined) {
        updates['repurposing.analytics.avgEngagement'] = metrics.engagement;
      }

      await updateDoc(docRef, updates);
    } catch (error) {
      console.error('Error updating variation performance:', error);
      throw new Error('Failed to update performance metrics');
    }
  }
}

// Export singleton instance
export const contentManagement = new ContentManagementService();
